import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { X, Plus, Save, Edit3, Trash2, FileText } from "lucide-react";

const parseCard = (item) => {
  try {
    const data = JSON.parse(item.content);
    return { ...item, desc: data.desc || "", body: data.body || "" };
  } catch {
    return { ...item, desc: item.content, body: "" };
  }
};

export default function DashboardInfoCards({ isAdmin }) {
  const [cards, setCards] = useState([]);
  const [openCard, setOpenCard] = useState(null);
  const [editCard, setEditCard] = useState(null);
  const [form, setForm] = useState({ title: "", desc: "", body: "" });

  useEffect(() => { fetchCards(); }, []);

  const fetchCards = async () => {
    try {
      const res = await axios.get(`${API}/content/dashboard-cards`);
      setCards(res.data.map(parseCard));
    } catch (e) { console.error(e); }
  };

  const openEdit = (card, e) => {
    if (e) e.stopPropagation();
    setEditCard(card ? card.id : "new");
    setForm(card ? { title: card.title, desc: card.desc, body: card.body } : { title: "", desc: "", body: "" });
  };

  const saveCard = async () => {
    if (!form.title.trim()) { toast.error("Inserisci un titolo"); return; }
    const content = JSON.stringify({ desc: form.desc.trim(), body: form.body });
    try {
      if (editCard === "new") {
        await axios.post(`${API}/content`, {
          section: "dashboard-cards",
          content_type: "card",
          title: form.title.trim(),
          content,
          order: cards.length
        });
        toast.success("Card aggiunta");
      } else {
        await axios.put(`${API}/content/${editCard}`, { title: form.title.trim(), content });
        toast.success("Card aggiornata");
      }
      setEditCard(null);
      fetchCards();
    } catch { toast.error("Errore"); }
  };

  const deleteCard = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/content/${id}`);
      toast.success("Eliminata");
      fetchCards();
    } catch { toast.error("Errore"); }
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-10 sm:mt-16 animate-fadeIn stagger-3">
        {cards.map((card, index) => (
          <button
            key={card.id}
            onClick={() => setOpenCard(card)}
            className="glass-card p-5 sm:p-6 text-center transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] relative group cursor-pointer"
            style={{ borderColor: "rgba(47, 216, 168, 0.2)" }}
            data-testid={`feature-card-${index}`}
          >
            {isAdmin && (
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <span onClick={(e) => openEdit(card, e)} className="p-1.5 rounded cursor-pointer" style={{ backgroundColor: "rgba(47,216,168,0.2)" }} data-testid={`edit-card-btn-${index}`}>
                  <Edit3 size={13} style={{ color: "var(--mm-gold)" }} />
                </span>
                <span onClick={(e) => deleteCard(card.id, e)} className="p-1.5 rounded cursor-pointer" style={{ backgroundColor: "rgba(239,68,68,0.2)" }} data-testid={`delete-card-btn-${index}`}>
                  <Trash2 size={13} style={{ color: "#EF4444" }} />
                </span>
              </div>
            )}
            <h3 
              className="text-lg sm:text-xl font-semibold mb-2 gold-text"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {card.title}
            </h3>
            <p className="text-sm sm:text-base" style={{ color: "var(--mm-text-secondary)" }}>
              {card.desc}
            </p>
          </button>
        ))}

        {isAdmin && (
          <button
            onClick={() => openEdit(null)}
            className="glass-card p-5 sm:p-6 flex flex-col items-center justify-center transition-all duration-300 hover:scale-[1.02]"
            style={{ borderColor: "var(--mm-border)", borderStyle: "dashed", minHeight: "100px" }}
            data-testid="add-card-btn"
          >
            <Plus size={26} style={{ color: "var(--mm-gold)", marginBottom: "6px" }} />
            <span className="text-sm" style={{ color: "var(--mm-text-secondary)", fontFamily: "'Space Grotesk', sans-serif" }}>
              Aggiungi
            </span>
          </button>
        )}
      </div>

      {/* Read Modal */}
      {openCard && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.75)", backdropFilter: "blur(8px)" }}
          onClick={() => setOpenCard(null)}
          data-testid="card-modal-overlay"
        >
          <div
            className="relative w-full max-w-lg rounded-xl p-6 sm:p-8 animate-fadeIn max-h-[85vh] overflow-y-auto"
            style={{
              backgroundColor: "var(--mm-surface, #111111)",
              border: "1px solid rgba(47, 216, 168, 0.4)",
              boxShadow: "0 8px 60px rgba(47, 216, 168, 0.15)"
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="card-modal"
          >
            <button
              onClick={() => setOpenCard(null)}
              className="absolute top-3 right-3 p-2 rounded-full transition-all duration-300 hover:scale-110"
              style={{ backgroundColor: "rgba(255, 255, 255, 0.08)", border: "1px solid var(--mm-border)" }}
              aria-label="Chiudi"
              data-testid="card-modal-close-btn"
            >
              <X size={18} style={{ color: "var(--mm-text-primary)" }} />
            </button>
            <div className="flex items-center gap-3 mb-5 pr-8">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(47, 216, 168, 0.15)" }}>
                <FileText size={22} style={{ color: "var(--mm-gold)" }} />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold gold-text uppercase" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.05em" }} data-testid="card-modal-title">
                {openCard.title}
              </h2>
            </div>
            <p className="text-sm sm:text-base leading-relaxed whitespace-pre-line" style={{ color: "var(--mm-text-secondary)" }} data-testid="card-modal-body">
              {openCard.body || openCard.desc}
            </p>
          </div>
        </div>
      )}

      {/* Admin Edit Modal */}
      {editCard && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.75)", backdropFilter: "blur(8px)" }}
          data-testid="card-edit-modal-overlay"
        >
          <div
            className="relative w-full max-w-lg rounded-xl p-6 animate-fadeIn max-h-[85vh] overflow-y-auto space-y-3"
            style={{
              backgroundColor: "var(--mm-surface, #111111)",
              border: "1px solid rgba(47, 216, 168, 0.4)"
            }}
            data-testid="card-edit-modal"
          >
            <h3 className="text-lg font-semibold" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>
              {editCard === "new" ? "Nuova Card" : "Modifica Card"}
            </h3>
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="admin-input w-full" placeholder="Titolo del tasto (es. Regolamento)" data-testid="card-form-title" />
            <input type="text" value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} className="admin-input w-full" placeholder="Sottotitolo breve (opzionale)" data-testid="card-form-desc" />
            <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="admin-textarea w-full" rows={8} placeholder="Testo completo mostrato nel popup (es. regole, privacy, termini...)" data-testid="card-form-body" />
            <div className="flex gap-3">
              <button onClick={saveCard} className="btn-primary flex-1 py-2 text-sm flex items-center justify-center gap-2" data-testid="card-form-save-btn">
                <Save size={14} /> Salva
              </button>
              <button onClick={() => setEditCard(null)} className="btn-secondary flex-1 py-2 text-sm" data-testid="card-form-cancel-btn">
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
